/**
 * Feature Flag Store
 *
 * Manages runtime feature flags fetched from the backend.
 * Flags are fetched on app load and cached in memory.
 * Uses stale-while-revalidate pattern (refreshes on window focus).
 *
 * Usage:
 * ```typescript
 * import { useFeatureFlagStore } from './stores/featureFlagStore'
 *
 * // In a component
 * const isEnabled = useFeatureFlagStore(s => s.isEnabled('new_chat_ui'))
 * ```
 */

import { create } from 'zustand'
import { flagsApi, tokenManager } from '../lib/api'

interface FeatureFlagState {
  flags: Record<string, boolean>
  isLoaded: boolean
  lastFetched: number

  // Actions
  loadFlags: () => Promise<void>
  isEnabled: (key: string) => boolean
}

export const useFeatureFlagStore = create<FeatureFlagState>((set, get) => ({
  flags: {},
  isLoaded: false,
  lastFetched: 0,

  loadFlags: async () => {
    if (!tokenManager.get()) return

    try {
      const response = await flagsApi.list()
      if (response.success && response.data) {
        set({
          flags: response.data,
          isLoaded: true,
          lastFetched: Date.now(),
        })
      }
    } catch {
      // Silently fail — feature flags are non-critical
      // Features default to disabled when flags can't be fetched
    }
  },

  isEnabled: (key: string) => {
    return get().flags[key] ?? false
  },
}))

/**
 * Set up stale-while-revalidate: refresh flags on window focus.
 */
if (globalThis.window !== undefined) {
  window.addEventListener('focus', () => {
    const store = useFeatureFlagStore.getState()
    const staleThreshold = 30_000 // 30 seconds
    if (Date.now() - store.lastFetched > staleThreshold) {
      store.loadFlags()
    }
  })
}
